import { useEffect, useState, useCallback } from "react";
import { getPublicConfig } from "../lib/api";

let configCache = null;
let configPromise = null;

function loadConfig() {
  if (configCache) return Promise.resolve(configCache);
  if (!configPromise) {
    configPromise = getPublicConfig()
      .then((c) => { configCache = c; return c; })
      .catch(() => ({ recaptcha_site_key: "", tawk_enabled: false }));
  }
  return configPromise;
}

let scriptLoaded = false;
let scriptPromise = null;

function loadRecaptchaScript(siteKey) {
  if (!siteKey) return Promise.resolve(false);
  if (scriptLoaded) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    if (window.grecaptcha?.execute) {
      scriptLoaded = true;
      resolve(true);
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      scriptLoaded = true;
      resolve(true);
    };
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Returns { ready, execute(action) -> token | "" }
 * If reCAPTCHA isn't configured (no site key), token comes back empty and the backend
 * gracefully accepts it (dev mode). In prod the backend rejects empty tokens.
 */
export function useRecaptcha() {
  const [siteKey, setSiteKey] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    loadConfig().then((c) => {
      if (!mounted) return;
      setSiteKey(c.recaptcha_site_key || "");
      if (c.recaptcha_site_key) {
        loadRecaptchaScript(c.recaptcha_site_key).then((ok) => mounted && setReady(ok));
      } else {
        setReady(true); // no captcha configured → just proceed
      }
    });
    return () => { mounted = false; };
  }, []);

  const execute = useCallback(
    async (action = "submit") => {
      if (!siteKey || !window.grecaptcha?.execute) return "";
      try {
        await new Promise((r) => window.grecaptcha.ready(r));
        return await window.grecaptcha.execute(siteKey, { action });
      } catch (e) {
        return "";
      }
    },
    [siteKey]
  );

  return { ready, execute, enabled: !!siteKey };
}
